import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Package, ArrowDownUp, Fish, Anchor, BarChart3, Users, LogOut, Snowflake, Plus, Menu, Calculator, Landmark, ScanLine, Smartphone, Settings, Award, Sparkles,
} from "lucide-react";
import { InstallButton } from "@/components/InstallButton";
import { COMPANY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/NotificationBell";

export const MENU = [
  { name: "Dashboard Ringkasan", icon: LayoutDashboard, to: "/", id: "dashboard", perms: ["dashboard_view"] },
  { name: "Inventaris Stok Ikan", icon: Package, to: "/inventory", id: "inventory", perms: ["inventory_view"] },
  { name: "Stok Tersedia", icon: Fish, to: "/stock-view", id: "stock-view", perms: ["stock_public_view"] },
  { name: "Stok Masuk / Keluar", icon: ArrowDownUp, to: "/transactions", id: "transactions", perms: ["stock_in", "stock_out", "sale_price", "tx_delete"] },
  { name: "Mode Scan Keluar", icon: ScanLine, to: "/scan", id: "scan", perms: ["scan_out"] },
  { name: "Pembayaran Masuk", icon: Landmark, to: "/payments", id: "payments", perms: ["payments_view"] },
  { name: "Master Jenis & Size", icon: Fish, to: "/master-species", id: "species", perms: ["master_view", "master_manage"] },
  { name: "Master Asal Ikan", icon: Anchor, to: "/master-origins", id: "origins", perms: ["master_view", "master_manage"] },
  { name: "Master Grade", icon: Award, to: "/master-grades", id: "grades", perms: ["master_view", "master_manage"] },
  { name: "Laporan & Analisis", icon: BarChart3, to: "/reports", id: "reports", perms: ["reports_view"] },
  { name: "Asisten AI", icon: Sparkles, to: "/assistant", id: "assistant", perms: ["ai_use"] },
  { name: "Akuntansi", icon: Calculator, to: "/accounting", id: "accounting", perms: ["accounting_view"] },
  { name: "Kelola Pengguna", icon: Users, to: "/users", id: "users", perms: ["users_manage"] },
  { name: "Pengaturan", icon: Settings, to: "/settings", id: "settings", perms: ["settings_manage"] },
];

const TITLES = {
  "/": "Dashboard Ringkasan", "/inventory": "Inventaris Stok Ikan", "/transactions": "Stok Masuk / Keluar", "/scan": "Mode Scan Keluar",
  "/master-species": "Master Jenis Ikan & Size", "/master-origins": "Master Asal Ikan", "/master-grades": "Master Grade", "/assistant": "Asisten AI", "/stock-view": "Stok Tersedia",
  "/reports": "Laporan & Analisis", "/users": "Kelola Pengguna", "/accounting": "Akuntansi", "/payments": "Pembayaran Masuk", "/settings": "Pengaturan",
};

function Sidebar({ onNavigate }) {
  const { user, logout, can } = useAuth();
  return (
    <aside className="h-full w-64 bg-slate-900 text-slate-100 flex flex-col border-r border-slate-800" data-testid="sidebar">
      <div className="px-5 pt-6 pb-5">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center p-1 shadow-[0_0_0_1px_rgba(56,189,248,0.35)]">
            <img src={COMPANY.logo} alt="Logo PT Cahaya Fish Bali" className="h-full w-full object-contain" data-testid="sidebar-logo" />
          </div>
          <div>
            <div className="font-extrabold tracking-tight text-sm leading-tight uppercase" data-testid="brand-name">{COMPANY.name}</div>
            <div className="text-[11px] text-slate-400">Ocean Cold WMS</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {MENU.filter((m) => m.perms.some(can)).map((m) => (
          <NavLink key={m.to} to={m.to} end={m.to === "/"} onClick={onNavigate}
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid={`nav-item-${m.id}`}>
            <m.icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />
            <span>{m.name}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-full bg-sky-600 flex items-center justify-center font-bold text-sm">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
          </div>
        </div>
        <NavLink to="/pasang" className="w-full flex items-center justify-center gap-2 text-xs py-2 mb-2 rounded-lg text-sky-300 hover:bg-slate-800 transition-colors" data-testid="link-install-guide"><Smartphone className="h-4 w-4" /> Cara pasang di HP</NavLink>
        <button onClick={logout} data-testid="btn-logout"
          className="w-full flex items-center justify-center gap-2 text-sm py-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors">
          <LogOut className="h-4 w-4" /> Keluar
        </button>
      </div>
    </aside>
  );
}

export function Layout({ children }) {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen aqua-bg flex">
      <div className="hidden lg:block fixed inset-y-0 left-0 z-30"><Sidebar /></div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <Sidebar onNavigate={() => setOpen(false)} />
          <div className="flex-1 bg-slate-900/50" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex-1 lg:pl-64 flex flex-col min-w-0">
        <header className="glass border-b border-sky-100 sticky top-0 z-40" data-testid="header">
          <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
            <button className="lg:hidden p-2 rounded-lg hover:bg-sky-50" onClick={() => setOpen(true)} data-testid="btn-menu-toggle">
              <Menu className="h-5 w-5 text-slate-700" />
            </button>
            <h1 className="text-lg font-bold text-slate-900 tracking-tight truncate" data-testid="page-title">{TITLES[pathname] || "Ocean Cold WMS"}</h1>
            <div className="ml-auto flex items-center gap-2 sm:gap-3">
              <span className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-sky-100 text-sky-800" data-testid="badge-temperature">
                <Snowflake className="h-3.5 w-3.5" /> -22.5°C Optimal
              </span>
              <span className="inline-flex text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 capitalize" data-testid="badge-role">
                {user?.role_name || user?.role}
              </span>
              <InstallButton compact />
              <NotificationBell />
              {can("stock_in") && <Button size="sm" className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={() => navigate("/transactions?open=in")} data-testid="btn-quick-stock-in">
                <Plus className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Stok Masuk</span>
              </Button>}
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 lg:py-8">{children}</main>
      </div>
    </div>
  );
}
